import { useCallback, useRef, useState } from 'react';
import { fetchTokenFromLocalStorage } from 'utils/helperMethods';

export type JiraRequestPayload = Record<string, any>;

export type CreateJiraRequestOptions = {
    baseUrl?: string; // e.g., https://your-domain.atlassian.net
    path?: string; // defaults to /servicedeskapi/request
    headers?: Record<string, string>;
    authToken?: string;
};

export type CreateJiraRequestResult<T = any> = {
    data: T | null;
    error: Error | null;
    loading: boolean;
    createRequest: (payload: FormData, signal?: AbortSignal) => Promise<T | null>;
    reset: () => void;
};

const DEFAULT_PATH = '/rest/servicedeskapi/request';

export default function useCreateJiraRequest<T = any>(
    options: CreateJiraRequestOptions = {},
): CreateJiraRequestResult<T> {
    const { path = DEFAULT_PATH, headers = {}, authToken } = options;
    const [data, setData] = useState<T | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const abortRef = useRef<AbortController | null>(null);

    const reset = useCallback(() => {
        setData(null);
        setError(null);
        setLoading(false);
        abortRef.current?.abort();
        abortRef.current = null;
    }, []);

    const createRequest = useCallback(
        async (payload: FormData, signal?: AbortSignal) => {
            setLoading(true);
            setError(null);
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            const finalSignal = signal || controller.signal;

            try {
                const token = authToken ?? fetchTokenFromLocalStorage();
                const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

                const res = await fetch(`${process.env.REACT_APP_JIRA_SERVICE_URL}/jira/create_ticket`, {
                    method: 'POST',
                    headers: {
                        ...authHeaders,
                        ...headers,
                    },
                    body: payload,
                });

                if (res.status !== 200) {
                    const text = await res.text();
                    throw new Error(`Jira request failed: ${res.status} ${res.statusText} - ${text}`);
                }

                const json = await res.json();
                setData(json as T);
                return json as T;
            } catch (e: any) {
                if (e?.name === 'AbortError') return null;
                setError(e);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [authToken, headers],
    );

    return { data, error, loading, createRequest, reset };
}
